<template>
  <section class="activity-timeline">
    <div class="activity-timeline__header">
      <div>
        <h2 class="activity-timeline__title">
          Activities
        </h2>
        <p class="activity-timeline__subtitle">
          {{ periodActivities.length }} entries · {{ formatDuration(periodTotals.total) }} total
        </p>
      </div>

      <div class="activity-timeline__filters" aria-label="Activity filters">
        <div class="activity-timeline__mode" aria-label="Summary mode">
          <button
            v-for="mode in summaryModeOptions"
            :key="mode.value"
            type="button"
            :class="{ 'activity-timeline__mode-button--active': summaryMode === mode.value }"
            @click="summaryMode = mode.value"
          >
            {{ mode.label }}
          </button>
        </div>
        <div
          v-if="periodOptions.length"
          class="activity-timeline__pager"
          aria-label="Activity period navigation"
        >
          <button
            type="button"
            :disabled="!hasOlderPeriod"
            @click="goToOlderPeriod"
          >
            Older
          </button>
          <span>{{ summaryPeriodLabel }}</span>
          <button
            type="button"
            :disabled="!hasNewerPeriod"
            @click="goToNewerPeriod"
          >
            Newer
          </button>
        </div>
        <button
          v-for="filter in filterOptions"
          :key="filter.value"
          type="button"
          class="activity-timeline__filter"
          :class="{ 'activity-timeline__filter--active': activeFilter === filter.value }"
          @click="activeFilter = filter.value"
        >
          {{ filter.label }}
          <span>{{ filterCount(filter.value) }}</span>
        </button>
      </div>
    </div>

    <div class="activity-timeline__summary">
      <div
        v-for="item in summaryItems"
        :key="item.key"
        class="activity-timeline__summary-item"
      >
        <span>{{ item.label }}</span>
        <strong>{{ formatDuration(item.seconds) }}</strong>
      </div>
    </div>

    <section
      v-if="scheduleRows.length"
      class="activity-schedule"
    >
      <div class="activity-schedule__header">
        <div>
          <h3>Schedule</h3>
          <p>{{ summaryPeriodLabel }}</p>
        </div>
        <div class="activity-schedule__legend">
          <span
            v-for="legend in scheduleLegend"
            :key="legend.kind"
            :class="`activity-schedule__legend-item activity-schedule__legend-item--${legend.kind}`"
          >
            {{ legend.label }}
          </span>
        </div>
      </div>

      <div
        class="activity-schedule__viewport"
        aria-label="Scrollable activity schedule"
      >
        <div class="activity-schedule__label-column">
          <div class="activity-schedule__label-spacer" />
          <div
            v-for="row in scheduleRows"
            :key="`${row.date}-label`"
            class="activity-schedule__date"
          >
            <strong>{{ formatScheduleDay(row.date) }}</strong>
            <span>{{ formatDuration(row.totalSeconds) }}</span>
          </div>
        </div>

        <div
          ref="scheduleScroller"
          class="activity-schedule__scroll"
          :class="{ 'activity-schedule__scroll--dragging': isScheduleDragging }"
          tabindex="0"
          @pointerdown="startScheduleDrag"
          @pointermove="dragSchedule"
          @pointerup="stopScheduleDrag"
          @pointercancel="stopScheduleDrag"
          @pointerleave="stopScheduleDrag"
        >
          <div
            class="activity-schedule__timeline"
            :style="scheduleTimelineStyle"
          >
            <div class="activity-schedule__axis">
              <span
                v-for="tick in scheduleTicks"
                :key="tick.minute"
                :class="tick.className"
                :style="{ left: `${tick.leftPct}%` }"
              >
                {{ tick.label }}
              </span>
            </div>

            <div class="activity-schedule__rows">
              <div
                v-for="row in scheduleRows"
                :key="row.date"
                class="activity-schedule__row"
              >
                <div class="activity-schedule__track">
                  <span
                    v-for="line in scheduleGridLines"
                    :key="`${row.date}-${line.minute}`"
                    class="activity-schedule__grid-line"
                    :class="{ 'activity-schedule__grid-line--major': line.isMajor }"
                    :style="{ left: `${line.leftPct}%` }"
                  />
                  <div
                    v-for="segment in row.segments"
                    :key="segment.key"
                    class="activity-schedule__segment"
                    :class="`activity-schedule__segment--${segment.kind}`"
                    :style="{ left: `${segment.leftPct}%`, width: `${segment.widthPct}%` }"
                    @mouseenter="showSegmentTooltip($event, segment)"
                    @mousemove="moveSegmentTooltip($event)"
                    @mouseleave="hideSegmentTooltip"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div
      v-if="hoveredSegment"
      class="activity-schedule__tooltip"
      :style="tooltipStyle"
    >
      <div class="activity-schedule__tooltip-title">
        {{ hoveredSegment.subject }}
      </div>
      <div class="activity-schedule__tooltip-meta">
        {{ hoveredSegment.kindLabel }} · {{ hoveredSegment.durationLabel }}
      </div>
      <div class="activity-schedule__tooltip-meta">
        {{ hoveredSegment.timeLabel }}
      </div>
      <div
        v-if="hoveredSegment.company"
        class="activity-schedule__tooltip-company"
      >
        {{ hoveredSegment.company }}
      </div>
    </div>

    <div v-if="visibleGroupedDays.length" class="activity-timeline__days">
      <section
        v-for="day in visibleGroupedDays"
        :key="day.date"
        class="activity-day"
      >
        <button
          type="button"
          class="activity-day__header"
          @click="toggleDay(day.date)"
        >
          <div>
            <h3>{{ formatDateHeader(day.date) }}</h3>
            <p>{{ day.activities.length }} entries</p>
          </div>
          <div class="activity-day__summary">
            <strong>{{ formatDuration(day.totalSeconds) }}</strong>
            <span class="activity-day__chevron">{{ isDayExpanded(day.date) ? '-' : '+' }}</span>
          </div>
        </button>

        <ol
          v-if="isDayExpanded(day.date)"
          class="activity-day__list"
        >
          <li
            v-for="activity in day.activities"
            :key="activity.id"
            class="activity-entry"
            :class="`activity-entry--${activityKind(activity)}`"
          >
            <div class="activity-entry__time">
              <span>{{ formatActivityTime(activity.dateLogged) }}</span>
              <strong>{{ formatDuration(activityTotalSeconds(activity)) }}</strong>
            </div>

            <div class="activity-entry__body">
              <div class="activity-entry__main">
                <div class="activity-entry__title">
                  {{ activity.subject || 'Untitled activity' }}
                </div>
                <div class="activity-entry__context">
                  <a
                    v-if="activityLink(activity)"
                    :href="activityLink(activity)"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ activity.againstType || 'item' }} #{{ activity.againstId }}
                  </a>
                  <span v-else>
                    {{ activity.againstType || 'item' }} #{{ activity.againstId || 'unknown' }}
                  </span>
                  <span v-if="companyName(activity)"> - {{ companyName(activity) }}</span>
                </div>
              </div>

              <div class="activity-entry__meta">
                <span class="activity-entry__badge">
                  {{ activityKindLabel(activity) }}
                </span>
                <span v-if="Number(activity.billable)" class="activity-entry__duration">
                  {{ formatDuration(activity.billable) }} billable
                </span>
                <span v-if="Number(activity.nonBillable)" class="activity-entry__duration">
                  {{ formatDuration(activity.nonBillable) }} non-billable
                </span>
              </div>
            </div>
          </li>
        </ol>
      </section>
    </div>

    <div
      v-else
      class="activity-timeline__empty"
    >
      No activities found for the selected range.
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useEmployeeInsightsStore, getActivityNonBillableType } from '@/pinia/employeeInsightsStore'

const eiStore = useEmployeeInsightsStore()
const activeFilter = ref('all')
const summaryMode = ref('daily')
const selectedPeriodIndex = ref(0)
const hoveredSegment = ref(null)
const tooltipPosition = ref({ x: 0, y: 0 })
const expandedDays = ref({})
const scheduleScroller = ref(null)
const isScheduleDragging = ref(false)
const scheduleDragState = ref({
  pointerId: null,
  startX: 0,
  scrollLeft: 0
})

const summaryModeOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' }
]

const activities = computed(() => {
  return [...eiStore.activitiesList].sort((a, b) => Number(a.dateLogged || 0) - Number(b.dateLogged || 0))
})

const filterOptions = [
  { value: 'all', label: 'All' },
  { value: 'billable', label: 'Billable' },
  { value: 'client', label: 'Client' },
  { value: 'internal', label: 'Internal' },
  { value: 'pto', label: 'PTO' }
]

const filteredActivities = computed(() => {
  if (activeFilter.value === 'all') return activities.value
  return activities.value.filter(activity => activityKind(activity) === activeFilter.value)
})
const periodOptions = computed(() => {
  return buildPeriodOptions(
    eiStore.startDate,
    eiStore.endDate,
    summaryMode.value
  )
})

const periodActivities = computed(() => {
  if (!activePeriodKey.value) return []
  return filteredActivities.value.filter(activity => periodKeyForActivity(activity) === activePeriodKey.value)
})
const scopedActivities = computed(() => {
  if (!activePeriodKey.value) return []
  return activities.value.filter(activity => periodKeyForActivity(activity) === activePeriodKey.value)
})
const datesForActivePeriod = computed(() => periodDates(activePeriodKey.value, summaryMode.value))
const visibleGroupedDays = computed(() => {
  const groups = new Map(
    datesForActivePeriod.value.map((date) => [
      date,
      {
        date,
        activities: [],
        totalSeconds: 0
      }
    ])
  )

  for (const activity of periodActivities.value) {
    const date = isoDate(activity.dateLogged)
    if (!groups.has(date)) {
      groups.set(date, {
        date,
        activities: [],
        totalSeconds: 0
      })
    }

    const group = groups.get(date)
    group.activities.push(activity)
    group.totalSeconds += activityTotalSeconds(activity)
  }

  return [...groups.values()].sort((a, b) => b.date.localeCompare(a.date))
})
const periodTotals = computed(() => totalForActivities(periodActivities.value))

const summaryItems = computed(() => [
  { key: 'billable', label: 'Billable', seconds: periodTotals.value.billable },
  { key: 'client', label: 'Client Non-billable', seconds: periodTotals.value.client },
  { key: 'internal', label: 'Internal', seconds: periodTotals.value.internal },
  { key: 'pto', label: 'PTO', seconds: periodTotals.value.pto }
])

const activePeriodKey = computed(() => {
  return periodOptions.value[selectedPeriodIndex.value]?.value || ''
})

const todayPeriodKey = computed(() => {
  const today = new Date()
  const todayIso = [
    today.getFullYear(),
    String(today.getMonth() + 1).padStart(2, '0'),
    String(today.getDate()).padStart(2, '0')
  ].join('-')

  return summaryMode.value === 'weekly' ? weekKey(todayIso) : todayIso
})

const summaryPeriodLabel = computed(() => {
  if (!activePeriodKey.value) return 'No activity in the selected range'
  if (summaryMode.value === 'weekly') return formatWeekLabel(activePeriodKey.value)
  return formatDateHeader(activePeriodKey.value)
})

const hasOlderPeriod = computed(() => selectedPeriodIndex.value < periodOptions.value.length - 1)
const hasNewerPeriod = computed(() => selectedPeriodIndex.value > 0)

const scheduleLegend = [
  { kind: 'billable', label: 'Billable' },
  { kind: 'client', label: 'Client' },
  { kind: 'internal', label: 'Internal' },
  { kind: 'pto', label: 'PTO' }
]

const scheduleStepMinutes = 30
const scheduleDayMinutes = 24 * 60
const scheduleRangePaddingMinutes = 120
const scheduleMinRangeMinutes = 14 * 60
const scheduleMinTimelineWidth = 1800
const schedulePixelsPerMinute = 3.2
const scheduleTickIntervalMinutes = 120
const scheduleMinimumLabelGapPct = 3

const scheduleRange = computed(() => activityScheduleRange(periodActivities.value))
const scheduleWindowMinutes = computed(() => scheduleRange.value.end - scheduleRange.value.start)
const scheduleTimelineStyle = computed(() => ({
  width: `${Math.max(scheduleMinTimelineWidth, scheduleWindowMinutes.value * schedulePixelsPerMinute)}px`
}))

const scheduleTicks = computed(() => {
  const ticks = []
  const firstTick = roundUpToInterval(scheduleRange.value.start, scheduleTickIntervalMinutes)

  for (
    let minute = firstTick;
    minute <= scheduleRange.value.end;
    minute += scheduleTickIntervalMinutes
  ) {
    const clampedMinute = Math.min(minute, scheduleRange.value.end)
    ticks.push({
      minute: clampedMinute,
      label: formatMinuteLabel(clampedMinute),
      leftPct: scheduleMinuteToPct(clampedMinute),
      className: scheduleAxisLabelClass(clampedMinute)
    })
  }

  if (
    (!ticks.length || ticks[0].minute !== scheduleRange.value.start) &&
    (!ticks.length || ticks[0].leftPct >= scheduleMinimumLabelGapPct)
  ) {
    ticks.unshift({
      minute: scheduleRange.value.start,
      label: formatMinuteLabel(scheduleRange.value.start),
      leftPct: 0,
      className: 'activity-schedule__axis-label activity-schedule__axis-label--start'
    })
  }

  const lastTick = ticks[ticks.length - 1]
  if (
    (!lastTick || lastTick.minute !== scheduleRange.value.end) &&
    (!lastTick || 100 - lastTick.leftPct >= scheduleMinimumLabelGapPct)
  ) {
    ticks.push({
      minute: scheduleRange.value.end,
      label: formatMinuteLabel(scheduleRange.value.end),
      leftPct: 100,
      className: 'activity-schedule__axis-label activity-schedule__axis-label--end'
    })
  }

  return ticks
})

function scheduleMinuteToPct(minute) {
  return ((minute - scheduleRange.value.start) / scheduleWindowMinutes.value) * 100
}

function scheduleAxisLabelClass(minute) {
  if (minute === scheduleRange.value.start) {
    return 'activity-schedule__axis-label activity-schedule__axis-label--start'
  }

  if (minute === scheduleRange.value.end) {
    return 'activity-schedule__axis-label activity-schedule__axis-label--end'
  }

  return 'activity-schedule__axis-label'
}

const scheduleGridLines = computed(() => {
  const lines = []
  const firstLine = roundUpToInterval(scheduleRange.value.start, scheduleStepMinutes)

  for (
    let minute = firstLine;
    minute <= scheduleRange.value.end;
    minute += scheduleStepMinutes
  ) {
    if (minute === scheduleRange.value.start || minute === scheduleRange.value.end) continue

    lines.push({
      minute,
      leftPct: scheduleMinuteToPct(minute),
      isMajor: minute % scheduleTickIntervalMinutes === 0
    })
  }

  return lines
})

const scheduleRows = computed(() => {
  const rows = new Map(
    datesForActivePeriod.value.map((date) => [
      date,
      {
        date,
        segments: [],
        totalSeconds: 0
      }
    ])
  )

  for (const activity of periodActivities.value) {
    const date = isoDate(activity.dateLogged)
    if (!rows.has(date)) {
      rows.set(date, {
        date,
        segments: [],
        totalSeconds: 0
      })
    }

    const row = rows.get(date)
    row.totalSeconds += activityTotalSeconds(activity)
    const segment = scheduleSegment(activity, row.segments.length)
    if (segment) {
      row.segments.push(segment)
    }
  }

  return [...rows.values()].sort((a, b) => a.date.localeCompare(b.date))
})

watch(
  [summaryMode, periodOptions],
  () => {
    if (!periodOptions.value.length) {
      selectedPeriodIndex.value = 0
      return
    }

    const todayIndex = periodOptions.value.findIndex(option => option.value === todayPeriodKey.value)
    selectedPeriodIndex.value = todayIndex >= 0 ? todayIndex : 0
  },
  { immediate: true }
)

watch(
  [activePeriodKey, periodActivities],
  () => {
    scrollScheduleToStart()
  },
  { immediate: true }
)

watch(
  visibleGroupedDays,
  (days) => {
    const nextState = {}
    for (const day of days) {
      nextState[day.date] = expandedDays.value[day.date] ?? true
    }
    expandedDays.value = nextState
  },
  { immediate: true }
)

function goToOlderPeriod() {
  if (!hasOlderPeriod.value) return
  selectedPeriodIndex.value += 1
}

function goToNewerPeriod() {
  if (!hasNewerPeriod.value) return
  selectedPeriodIndex.value -= 1
}

function toggleDay(date) {
  expandedDays.value = {
    ...expandedDays.value,
    [date]: !expandedDays.value[date]
  }
}

function isDayExpanded(date) {
  return expandedDays.value[date] ?? true
}

function filterCount(filter) {
  if (filter === 'all') return scopedActivities.value.length
  return scopedActivities.value.filter(activity => activityKind(activity) === filter).length
}

function totalForActivities(source) {
  return source.reduce((totals, activity) => {
    const billable = Number(activity.billable || 0)
    const nonBillable = Number(activity.nonBillable || 0)
    const kind = activityKind(activity)

    totals.billable += billable
    totals.total += billable + nonBillable

    if (kind === 'client') totals.client += nonBillable
    if (kind === 'internal') totals.internal += nonBillable
    if (kind === 'pto') totals.pto += nonBillable

    return totals
  }, {
    billable: 0,
    client: 0,
    internal: 0,
    pto: 0,
    total: 0
  })
}

function activityKind(activity) {
  if (Number(activity.billable || 0) > 0) return 'billable'

  const breadcrumbs = Array.isArray(activity.breadcrumbs) ? activity.breadcrumbs : []
  const hasBenefits = breadcrumbs.some(crumb =>
    crumb?.table?.toString().toLowerCase() === 'milestone' &&
    crumb?.title?.toString().toLowerCase() === 'benefits'
  )
  const hasPto = breadcrumbs.some(crumb =>
    crumb?.table?.toString().toLowerCase() === 'task' &&
    crumb?.title?.toString().toLowerCase() === 'pto'
  )

  if (hasBenefits && hasPto) return 'pto'
  return getActivityNonBillableType(activity) === 'Internal' ? 'internal' : 'client'
}

function activityKindLabel(activity) {
  const labels = {
    billable: 'Billable',
    client: 'Client Non-billable',
    internal: 'Internal',
    pto: 'PTO'
  }
  return labels[activityKind(activity)]
}

function activityTotalSeconds(activity) {
  return Number(activity.billable || 0) + Number(activity.nonBillable || 0)
}

function isoDate(epochSeconds) {
  if (!epochSeconds) return 'unknown'
  return new Date(Number(epochSeconds) * 1000).toISOString().slice(0, 10)
}

function periodKeyForActivity(activity) {
  const date = isoDate(activity.dateLogged)
  if (date === 'unknown') return ''
  return summaryMode.value === 'weekly' ? weekKey(date) : date
}

function buildPeriodOptions(startEpoch, endEpoch, modeValue) {
  if (!startEpoch || !endEpoch) return []

  if (modeValue === 'weekly') {
    const weekStarts = []
    let cursor = startUtcDate(startEpoch)
    const end = startUtcDate(endEpoch)

    while (cursor <= end) {
      const key = weekKey(cursor.toISOString().slice(0, 10))
      if (!weekStarts.includes(key)) weekStarts.push(key)
      cursor.setUTCDate(cursor.getUTCDate() + 7)
    }

    return weekStarts
      .sort()
      .reverse()
      .map((key) => ({
        value: key,
        label: formatWeekLabel(key)
      }))
  }

  const dates = []
  let cursor = startUtcDate(startEpoch)
  const end = startUtcDate(endEpoch)

  while (cursor <= end) {
    dates.push(cursor.toISOString().slice(0, 10))
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }

  return dates
    .sort()
    .reverse()
    .map((key) => ({
      value: key,
      label: formatDateHeader(key)
    }))
}

function periodDates(key, modeValue) {
  if (!key) return []
  if (modeValue === 'daily') return [key]

  const [year, month, day] = key.split('-').map(Number)
  const start = new Date(Date.UTC(year, month - 1, day))
  const dates = []
  for (let offset = 0; offset < 7; offset += 1) {
    const current = new Date(start)
    current.setUTCDate(start.getUTCDate() + offset)
    dates.push(current.toISOString().slice(0, 10))
  }
  return dates
}

function startUtcDate(epochSeconds) {
  const date = new Date(Number(epochSeconds) * 1000)
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
}

function weekKey(isoString) {
  const [year, month, day] = isoString.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  const dayOfWeek = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() - dayOfWeek + 1)
  return date.toISOString().slice(0, 10)
}

function formatDateHeader(isoString) {
  if (isoString === 'unknown') return 'Unknown Date'
  const [year, month, day] = isoString.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  })
}

function formatActivityTime(epochSeconds) {
  if (!epochSeconds) return '--:--'
  return new Date(Number(epochSeconds) * 1000).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit'
  })
}

function formatScheduleDay(isoString) {
  if (isoString === 'unknown') return 'Unknown'
  const [year, month, day] = isoString.split('-').map(Number)
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  })
}

function formatWeekLabel(weekStartIso) {
  const [year, month, day] = weekStartIso.split('-').map(Number)
  const start = new Date(Date.UTC(year, month - 1, day))
  const end = new Date(start)
  end.setUTCDate(start.getUTCDate() + 6)
  const startLabel = start.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  })
  const endLabel = end.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC'
  })
  return `${startLabel} - ${endLabel}`
}

function formatMinuteLabel(totalMinutes) {
  const normalizedMinutes = ((totalMinutes % scheduleDayMinutes) + scheduleDayMinutes) % scheduleDayMinutes
  const hours24 = Math.floor(normalizedMinutes / 60)
  const minutes = normalizedMinutes % 60
  const suffix = hours24 >= 12 ? 'pm' : 'am'
  const hours12 = hours24 % 12 || 12
  return minutes === 0
    ? `${hours12}${suffix}`
    : `${hours12}:${String(minutes).padStart(2, '0')}${suffix}`
}

function roundDownToHalfHour(totalMinutes) {
  return Math.floor(totalMinutes / scheduleStepMinutes) * scheduleStepMinutes
}

function roundUpToInterval(totalMinutes, intervalMinutes) {
  return Math.ceil(totalMinutes / intervalMinutes) * intervalMinutes
}

function activityScheduleRange(source) {
  if (!source.length) {
    return {
      start: 7 * 60,
      end: 17 * 60
    }
  }

  const earliestMinute = source.reduce((lowest, activity) => {
    const start = new Date(Number(activity.dateLogged || 0) * 1000)
    const startMinute = start.getHours() * 60 + start.getMinutes()
    return Math.min(lowest, startMinute)
  }, scheduleDayMinutes)

  const latestMinute = source.reduce((highest, activity) => {
    const start = new Date(Number(activity.dateLogged || 0) * 1000)
    const startMinute = start.getHours() * 60 + start.getMinutes()
    const durationMinutes = Math.max(5, Math.round(activityTotalSeconds(activity) / 60))
    return Math.max(highest, startMinute + durationMinutes)
  }, 0)

  let start = roundDownToHalfHour(Math.max(0, earliestMinute - scheduleRangePaddingMinutes))
  let end = roundUpToInterval(Math.min(scheduleDayMinutes, latestMinute + scheduleRangePaddingMinutes), scheduleStepMinutes)

  if (end - start < scheduleMinRangeMinutes) {
    const extraMinutes = scheduleMinRangeMinutes - (end - start)
    const before = Math.ceil(extraMinutes / 2)
    const after = extraMinutes - before
    start = Math.max(0, start - before)
    end = Math.min(scheduleDayMinutes, end + after)

    if (end - start < scheduleMinRangeMinutes && start === 0) {
      end = Math.min(scheduleDayMinutes, scheduleMinRangeMinutes)
    }

    if (end - start < scheduleMinRangeMinutes && end === scheduleDayMinutes) {
      start = Math.max(0, scheduleDayMinutes - scheduleMinRangeMinutes)
    }
  }

  return { start, end }
}

function scheduleSegment(activity, index) {
  const start = new Date(Number(activity.dateLogged || 0) * 1000)
  const durationMinutes = Math.max(5, Math.round(activityTotalSeconds(activity) / 60))
  const startMinute = start.getHours() * 60 + start.getMinutes()
  const endMinute = startMinute + durationMinutes

  if (
    endMinute <= scheduleRange.value.start ||
    startMinute >= scheduleRange.value.end
  ) {
    return null
  }

  const clampedStart = Math.min(Math.max(startMinute, scheduleRange.value.start), scheduleRange.value.end)
  const clampedEnd = Math.min(Math.max(endMinute, scheduleRange.value.start), scheduleRange.value.end)
  const visibleMinutes = Math.max(5, clampedEnd - clampedStart)
  const leftPct = ((clampedStart - scheduleRange.value.start) / scheduleWindowMinutes.value) * 100
  const widthPct = Math.min(100 - leftPct, (visibleMinutes / scheduleWindowMinutes.value) * 100)

  return {
    key: `${activity.id || 'activity'}-${index}`,
    kind: activityKind(activity),
    kindLabel: activityKindLabel(activity),
    leftPct,
    widthPct: Math.max(0, widthPct),
    subject: activity.subject || 'Untitled activity',
    durationLabel: formatDuration(activityTotalSeconds(activity)),
    timeLabel: `${formatActivityTime(activity.dateLogged)} start`,
    company: companyName(activity)
  }
}

function scrollScheduleToStart() {
  const resetScroll = () => {
    if (scheduleScroller.value) {
      scheduleScroller.value.scrollLeft = 0
    }
  }

  setTimeout(resetScroll, 0)
}

function startScheduleDrag(event) {
  if (event.button !== 0 || !scheduleScroller.value) return
  event.preventDefault()
  hideSegmentTooltip()
  isScheduleDragging.value = true
  scheduleDragState.value = {
    pointerId: event.pointerId,
    startX: event.clientX,
    scrollLeft: scheduleScroller.value.scrollLeft
  }
  scheduleScroller.value.setPointerCapture?.(event.pointerId)
}

function dragSchedule(event) {
  if (!isScheduleDragging.value || !scheduleScroller.value) return
  event.preventDefault()
  const deltaX = event.clientX - scheduleDragState.value.startX
  scheduleScroller.value.scrollLeft = scheduleDragState.value.scrollLeft - deltaX
}

function stopScheduleDrag(event) {
  if (!isScheduleDragging.value) return
  scheduleScroller.value?.releasePointerCapture?.(scheduleDragState.value.pointerId || event.pointerId)
  isScheduleDragging.value = false
}

const tooltipStyle = computed(() => ({
  left: `${tooltipPosition.value.x}px`,
  top: `${tooltipPosition.value.y}px`
}))

function showSegmentTooltip(event, segment) {
  if (isScheduleDragging.value) return
  hoveredSegment.value = segment
  moveSegmentTooltip(event)
}

function moveSegmentTooltip(event) {
  if (isScheduleDragging.value) return
  tooltipPosition.value = {
    x: event.clientX + 14,
    y: event.clientY + 14
  }
}

function hideSegmentTooltip() {
  hoveredSegment.value = null
}

function formatDuration(seconds) {
  const totalMinutes = Math.max(0, Math.round(Number(seconds || 0) / 60))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours}h ${String(minutes).padStart(2, '0')}m`
}

function companyName(activity) {
  const breadcrumbs = Array.isArray(activity.breadcrumbs) ? activity.breadcrumbs : []
  return breadcrumbs.find(crumb => crumb?.table?.toString().toLowerCase() === 'company')?.title || ''
}

function activityLink(activity) {
  if (!activity.againstType || !activity.againstId) return ''
  return acceloUrl(activity.againstType, activity.againstId)
}

function acceloUrl(type, id) {
  const normalizedType = type?.toString().toLowerCase()
  const actionByType = {
    company: 'view_company',
    issue: 'view_issue',
    ticket: 'view_issue',
    job: 'view_job',
    project: 'view_job',
    milestone: 'view_milestone',
    task: 'view_task'
  }
  const action = actionByType[normalizedType] || `view_${normalizedType}`
  return `https://perbyte.accelo.com/?action=${action}&id=${id}`
}
</script>

<style scoped>
.activity-timeline {
  margin-top: 1.5rem;
  overflow: hidden;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.activity-timeline__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #f9fafb;
}

.activity-timeline__title {
  margin: 0;
  color: #001A70;
  font-size: 1.125rem;
  font-weight: 700;
}

.activity-timeline__subtitle {
  margin: 0.25rem 0 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.activity-timeline__filters {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.5rem;
}

.activity-timeline__filter {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  padding: 0.4rem 0.65rem;
  color: #001A70;
  font-size: 0.75rem;
  font-weight: 600;
}

.activity-timeline__filter span {
  border-radius: 999px;
  background: #eef2f7;
  padding: 0.05rem 0.4rem;
  color: #4b5563;
}

.activity-timeline__filter--active {
  border-color: #0DABCB;
  background: #0DABCB;
  color: #fff;
}

.activity-timeline__filter--active span {
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
}

.activity-timeline__mode {
  display: inline-flex;
  overflow: hidden;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
}

.activity-timeline__mode button {
  border: 0;
  background: transparent;
  padding: 0.4rem 0.65rem;
  color: #4b5563;
  font-size: 0.75rem;
  font-weight: 700;
}

.activity-timeline__mode button + button {
  border-left: 1px solid #cbd5e1;
}

.activity-timeline__mode-button--active {
  background: #001A70 !important;
  color: #fff !important;
}

.activity-timeline__period-select {
  min-width: 12rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  padding: 0.4rem 0.65rem;
  color: #001A70;
  font-size: 0.75rem;
  font-weight: 600;
}

.activity-timeline__pager {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #fff;
  padding: 0.2rem;
}

.activity-timeline__pager span {
  min-width: 11rem;
  padding: 0 0.45rem;
  color: #001A70;
  font-size: 0.75rem;
  font-weight: 700;
  text-align: center;
}

.activity-timeline__pager button {
  border: 0;
  border-radius: 4px;
  background: #f8fafc;
  padding: 0.35rem 0.55rem;
  color: #4b5563;
  font-size: 0.72rem;
  font-weight: 700;
}

.activity-timeline__pager button:hover:not(:disabled) {
  background: #e6f7fb;
  color: #001A70;
}

.activity-timeline__pager button:disabled {
  opacity: 0.45;
  cursor: default;
}

.activity-timeline__summary {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1px;
  background: #e5e7eb;
}

.activity-timeline__summary-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  background: #fff;
  padding: 0.75rem 1rem;
}

.activity-timeline__summary-item span {
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
}

.activity-timeline__summary-item strong {
  color: #001A70;
  font-variant-numeric: tabular-nums;
}

.activity-schedule {
  border-top: 1px solid #e5e7eb;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  padding: 1rem;
}

.activity-schedule__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.activity-schedule__header h3 {
  margin: 0;
  color: #001A70;
  font-size: 0.95rem;
  font-weight: 700;
}

.activity-schedule__header p {
  margin: 0.2rem 0 0;
  color: #6b7280;
  font-size: 0.8rem;
}

.activity-schedule__legend {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.45rem;
}

.activity-schedule__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: #4b5563;
  font-size: 0.72rem;
  font-weight: 700;
}

.activity-schedule__legend-item::before {
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 999px;
  content: "";
}

.activity-schedule__legend-item--billable::before {
  background: #83BD48;
}

.activity-schedule__legend-item--client::before {
  background: #0DABCB;
}

.activity-schedule__legend-item--internal::before {
  background: #3C9CA4;
}

.activity-schedule__legend-item--pto::before {
  background: #94AC7C;
}

.activity-schedule__axis {
  position: relative;
  height: 1rem;
  margin-bottom: 0.55rem;
  color: #6b7280;
  font-size: 0.7rem;
  font-weight: 700;
}

.activity-schedule__axis-label {
  position: absolute;
  transform: translateX(-50%);
  white-space: nowrap;
  text-align: center;
}

.activity-schedule__axis-label--start {
  transform: none;
  text-align: left;
}

.activity-schedule__axis-label--end {
  transform: translateX(-100%);
  text-align: right;
}

.activity-schedule__rows {
  display: grid;
  gap: 0.5rem;
}

.activity-schedule__row {
  display: block;
}

.activity-schedule__date {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.1rem;
  height: 1.6rem;
  color: #001A70;
  font-size: 0.75rem;
}

.activity-schedule__date span {
  color: #6b7280;
  font-variant-numeric: tabular-nums;
}

.activity-schedule__track {
  position: relative;
  height: 1.6rem;
  overflow: hidden;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
}

.activity-schedule__grid-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(148, 163, 184, 0.16);
  pointer-events: none;
}

.activity-schedule__grid-line--major {
  background: rgba(148, 163, 184, 0.32);
}

.activity-schedule__segment {
  position: absolute;
  top: 0.25rem;
  height: 1rem;
  min-width: 0.35rem;
  border-radius: 999px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.16);
  cursor: pointer;
}

.activity-schedule__segment--billable {
  background: #83BD48;
}

.activity-schedule__segment--client {
  background: #0DABCB;
}

.activity-schedule__segment--internal {
  background: #3C9CA4;
}

.activity-schedule__segment--pto {
  background: #94AC7C;
}

.activity-schedule__tooltip {
  position: fixed;
  z-index: 50;
  max-width: 18rem;
  pointer-events: none;
  border: 1px solid rgba(148, 163, 184, 0.35);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.98);
  padding: 0.65rem 0.75rem;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
  backdrop-filter: blur(8px);
}

.activity-schedule__tooltip-title {
  color: #001A70;
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.35;
}

.activity-schedule__tooltip-meta {
  margin-top: 0.2rem;
  color: #4b5563;
  font-size: 0.74rem;
  font-weight: 600;
}

.activity-schedule__tooltip-company {
  margin-top: 0.35rem;
  color: #0DABCB;
  font-size: 0.72rem;
  font-weight: 700;
}

.activity-schedule__viewport {
  display: grid;
  grid-template-columns: 112px minmax(0, 1fr);
  gap: 1rem;
  min-width: 0;
}

.activity-schedule__label-column {
  display: grid;
  flex: none;
  gap: 0.5rem;
}

.activity-schedule__label-spacer {
  height: 1.55rem;
}

.activity-schedule__scroll {
  min-width: 0;
  overflow-x: auto;
  overflow-y: visible;
  overscroll-behavior-x: contain;
  cursor: grab;
  scrollbar-gutter: stable;
  touch-action: pan-y;
  -webkit-overflow-scrolling: touch;
  user-select: none;
}

.activity-schedule__scroll--dragging {
  cursor: grabbing;
}

.activity-schedule__scroll--dragging .activity-schedule__segment {
  cursor: grabbing;
}

.activity-schedule__scroll:focus-visible {
  outline: 2px solid #0DABCB;
  outline-offset: 3px;
}

.activity-schedule__timeline {
  min-width: 100%;
  padding-bottom: 0.35rem;
}

.activity-timeline__days {
  padding: 1rem;
}

.activity-day + .activity-day {
  margin-top: 1.25rem;
}

.activity-day__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  margin-bottom: 0.75rem;
  border: 0;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.activity-day__header h3 {
  margin: 0;
  color: #001A70;
  font-size: 0.95rem;
  font-weight: 700;
}

.activity-day__header p {
  margin: 0.2rem 0 0;
  color: #6b7280;
  font-size: 0.8rem;
}

.activity-day__header strong {
  color: #001A70;
  font-variant-numeric: tabular-nums;
}

.activity-day__summary {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.activity-day__chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.35rem;
  height: 1.35rem;
  border-radius: 999px;
  background: #e6f7fb;
  color: #001A70;
  font-size: 0.95rem;
  font-weight: 700;
  line-height: 1;
}

.activity-day__list {
  position: relative;
  margin: 0;
  padding: 0;
  list-style: none;
}

.activity-entry {
  display: grid;
  grid-template-columns: 84px minmax(0, 1fr);
  gap: 1rem;
  position: relative;
}

.activity-entry + .activity-entry {
  margin-top: 0.75rem;
}

.activity-entry__time {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.2rem;
  padding-top: 0.75rem;
  color: #6b7280;
  font-size: 0.75rem;
  font-variant-numeric: tabular-nums;
}

.activity-entry__time strong {
  color: #001A70;
}

.activity-entry__body {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-left-width: 4px;
  border-radius: 6px;
  background: #fff;
  padding: 0.75rem 1rem;
}

.activity-entry--billable .activity-entry__body {
  border-left-color: #83BD48;
}

.activity-entry--client .activity-entry__body {
  border-left-color: #0DABCB;
}

.activity-entry--internal .activity-entry__body {
  border-left-color: #3C9CA4;
}

.activity-entry--pto .activity-entry__body {
  border-left-color: #94AC7C;
}

.activity-entry__main {
  min-width: 0;
}

.activity-entry__title {
  color: #001A70;
  font-size: 0.95rem;
  font-weight: 700;
}

.activity-entry__context {
  margin-top: 0.25rem;
  color: #6b7280;
  font-size: 0.8rem;
}

.activity-entry__context a {
  color: #0DABCB;
  font-weight: 600;
}

.activity-entry__context a:hover {
  text-decoration: underline;
}

.activity-entry__meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.4rem;
  max-width: 320px;
}

.activity-entry__badge,
.activity-entry__duration {
  border-radius: 999px;
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  color: #374151;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.activity-entry__badge {
  background: #e6f7fb;
  color: #001A70;
}

.activity-timeline__empty {
  padding: 2rem;
  color: #6b7280;
  text-align: center;
}

@media (max-width: 900px) {
  .activity-timeline__header,
  .activity-entry__body {
    flex-direction: column;
  }

  .activity-timeline__summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .activity-schedule__header {
    flex-direction: column;
  }

  .activity-schedule__viewport {
    grid-template-columns: 92px minmax(0, 1fr);
    gap: 0.75rem;
  }

  .activity-entry {
    grid-template-columns: 1fr;
  }

  .activity-entry__time {
    align-items: flex-start;
    padding-top: 0;
  }
}
</style>
